import React, { useEffect, useState, useContext, useRef } from 'react'
import { useReactToPrint } from 'react-to-print';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
    Container,
    Stack,
    Typography,
    Button,
    TextField,
    Autocomplete,
    Tabs,
    Tab,
    IconButton,
    RadioGroup,
    Radio,
    FormControl,
} from '@mui/material'
import api from '../../api';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Iconify from '../iconify/Iconify';
import PrintQRComponent from './printQR';
import PrintInvoiceComponent from './printInvoice';

// Constants and Configuration
const PACKAGE_TYPES = [
        { label: 'Local', value: 'Local' },
        { label: 'State', value: 'State' },
        { label: 'National', value: 'National' },
        { label: 'International', value: 'International' },
];

const VALIDATION_PATTERNS = {
    mobile: /^([0|+[0-9]{1,5})?([7-9][0-9]{9})$/,
    email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
};

const INITIAL_PACKAGE_STATE = {
        clientName: '',
        senderName: '',
        gstNo: '',
        senderPinCode: '',
        senderAddress: '',
        senderDistrict: '',
        senderState: '',
        senderCountry: '',
        senderContactNo: '',
        senderEmail: '',
        senderWhatsappNo: '',
        consigneeName: '',
        receiverName: "",
        clientId: '',
        consigneePinCode: '',
        consigneeAddress: '',
        consigneeDistrict: '',
        consigneeState: '',
        consigneeCountry: '',
        consigneeContactNo: '',
        consigneeEmail: '',
        consigneeWhatsappNo: '',
        consignmentNo:'',
        consigneeId: '',
        packageType: 'Local',
        weight: '100',
        amount: '0',
        discount: '0',
        totalAmount: '0',
        adminId: '',
        employeeName: '',
        emloyeeId: '',
        cGSTAmount: '',
        sGSTAmount: '',
        iGSTAmount: '',
        totalGST: '0',
        toPay: false,
        paymentType: 'Credit',
        subPackages: []
};

const INITIAL_ERROR_STATE = {
        clientName: '',
        senderName: '',
        gstNo: '',
        senderPinCode: '',
        senderAddress: '',
        senderDistrict: '',
        senderState: '',
        senderCountry: '',
        senderContactNo: '',
        senderEmail: '',
        senderWhatsappNo: '',
        consigneeName: '',
        consigneePinCode: '',
        consigneeAddress: '',
        consigneeDistrict: '',
        consigneeState: '',
        consigneeCountry: '',
        consigneeContactNo: '',
        consigneeEmail: '',
        consigneeWhatsappNo: '',
        packageType: '',
        weight: '',
        amount: '',
        discount: '',
        consignmentNo:'',
        totalAmount: '',
    totalGST: "",
        adminId: '',
        employeeName: '',
        emloyeeId: ''
};

// Validation Functions
const validateField = (name, value) => {
    const mobilePattern = VALIDATION_PATTERNS.mobile;
    const emailPattern = VALIDATION_PATTERNS.email;
    
    switch (name) {
        case 'consigneeName':
        case 'gstNo':
        case 'consigneeDistrict':
        case 'consigneeState':
        case 'consigneeCountry':
        case 'senderDistrict':
        case 'consignmentNo':
        case 'senderState':
        case 'senderCountry':
            return value.length > 1 ? '' : 'Minimum two digits required!';
        
        case 'consigneeAddress':
        case 'senderAddress':
            return value.length > 1 ? '' : 'Full address is required!';
        
        case 'consigneePinCode':
        case 'senderPinCode':
            return value.length === 6 ? '' : 'Correct pincode is required!';
        
        case 'consigneeContactNo':
        case 'senderContactNo':
        case 'consigneeWhatsappNo':
        case 'senderWhatsappNo':
            return mobilePattern.test(value) ? '' : 'Incorrect mobile number!';
        
        case 'consigneeEmail':
        case 'senderEmail':
            return emailPattern.test(String(value).toLowerCase()) ? '' : 'Email is incorrect!';
        
        case 'weight':
            return value.length > 1 ? '' : 'Add Weight';
        
        case 'amount':
            return parseFloat(value) > 0 ? '' : 'Enter Correct Amount';
        
        case 'totalGST':
            return parseFloat(value) >= 0 ? '' : 'Enter Correct GST Amount';
        
        case 'discount':
            return parseFloat(value) >= 0 ? '' : 'Enter Correct Discount Amount';
        
        case 'totalAmount':
            return parseFloat(value) > 0 ? '' : 'Enter Correct Total Amount';
        
        default:
            return '';
    }
};

// API Functions
const fetchClients = async () => {
    try {
        const response = await api.get('/api/v1/clients');
        if (response.data.success) {
            const clients = response.data.clients.map((item) => ({
                label: `${item.name} (${item.gstNo})`,
                value: item
            }));
            clients.push({ label: 'General', value: '' });
            return clients;
        } else {
            toast.error(response.data.message);
            return [];
        }
    } catch (error) {
        toast.error('Something went wrong!');
        return [];
    }
};

const fetchPincodeData = async (pincode) => {
    try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();
        return data[0];
    } catch (error) {
        return null;
    }
};

const getApiErrorMessage = (error, fallback = 'Something went wrong!') =>
    error?.response?.data?.message || error?.message || fallback;

const submitPackage = async (packageData) => {
    try {
        const response = await api.post('/api/v1/packages', { data: packageData });
        if (response.data.success) {
            return response.data.package;
        } else {
            throw new Error(response.data.message);
        }
    } catch (error) {
        throw new Error(getApiErrorMessage(error, 'Failed to add package. Please try again.'));
    }
};

const updatePackage = async (packageData) => {
    const response = await api.put(`/api/v1/packages`, { data: packageData });
    if (response.data.success) {
        return response.data.package;
    } else {
        throw new Error(response.data.message);
    }
};      

export default function AddPackageComponent() {

    const users = JSON.parse(localStorage.getItem('user'));
    const { state } = useLocation();
    const navigate = useNavigate();
    // Consolidated state management
    const [clients, setClients] = useState([]);
    const [consigneeClients, setConsigneeClients] = useState([]);
    const [packageData, setPackage] = useState(INITIAL_PACKAGE_STATE);
    const [packageErrors, setPackageError] = useState(INITIAL_ERROR_STATE);
    const [responseData, setResponseData] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState(-1);
    const [editData, setEditData] = useState(state);
    const [showEditData, setShowEditData] = useState(false);
    
    // UI state
    const [uiState, setUiState] = useState({
        showQR: false,
        showInvoice: false,
        showSenderForm: true,
        showConsigneeForm: false,
        isPrinting: false,
        isPrintingInvoice: false
    });
    
    // Calculation state
    const [calculation, setCalculation] = useState({
        localRate: "",
        stateRate: "",
        nationalRate: "",
        interNationalRate: ""
    });

    useEffect(() => {
        getClients()
    }, []);

    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleAddTab = () => {
        const newTabs = [...tabs, { label: `Package ${tabs.length + 1}`, data: `Data ${tabs.length + 1}` }];
        setTabs(newTabs);
        setActiveTab(newTabs.length - 1);
    };

    const handleDeleteTab = (index) => {
        const newTabs = tabs.filter((tab, i) => i !== index);
        setTabs(newTabs);

        // Adjust activeTab if the deleted tab is the active tab
        if (index === activeTab && newTabs.length > 0) {
            const newActiveTab = index > 0 ? index - 1 : 0;
            setActiveTab(newActiveTab);
        }
    };
    const handleDataChange = (index, value) => {
        const newTabs = [...tabs];
        newTabs[index].data = value;
        setTabs(newTabs);
    };
    const getClients = async () => {
        const clientsData = await fetchClients();
        setClients(clientsData);
        setConsigneeClients(clientsData); // Both sender and consignee use the same client list
    };


    const onClientChange = (event, values) => {
        if (!values) return;
        
        if (values.label === "General") {
            setUiState(prev => ({ ...prev, showSenderForm: true }));
            setPackage(prev => ({
                ...prev,
                clientName: "General",
                senderName: '',
                gstNo: '',
                senderPinCode: '',
                senderAddress: '',
                senderDistrict: '',
                senderState: '',
                senderCountry: '',
                senderContactNo: '',
                senderEmail: '',
                senderWhatsappNo: '',
                senderId: 'General',
            }));
        } else if (values.value) {
            setUiState(prev => ({ ...prev, showSenderForm: false }));
            setPackage(prev => ({
                ...prev,
                clientName: values.label, // Store the label for display
                senderName: '',
                gstNo: values.value.gstNo,
                senderPinCode: values.value.pinCode,
                senderAddress: values.value.address,
                senderDistrict: values.value.district,
                senderState: values.value.state,
                senderCountry: values.value.country,
                senderContactNo: values.value.contactNo,
                senderEmail: values.value.email,
                senderWhatsappNo: values.value.whatsappNo,
                senderId: values.value.id
            }));
            setCalculation({
                localRate: values.value.localRate,
                stateRate: values.value.stateRate,
                nationalRate: values.value.nationalRate,
                interNationalRate: values.value.interNationalRate
            });
        }
    };

    useEffect(() => {
        calculateAmount();
    }, [calculation]);

   
    useEffect(() => {
        const changeAmount = parseFloat(packageData.totalAmount) - parseFloat(packageData.discount);
        if (changeAmount < 0) {
            setPackageError(prev => ({
                ...prev,
                totalAmount: "Total Amount cannot be negative",
                discount: "This discount amount is not valid"
            }));
            setPackage(prev => ({ ...prev, totalAmount: changeAmount }));
        } else if (packageData.discount === "" || packageData.discount < 0) {
            setPackageError(prev => ({
                ...prev,
                totalAmount: "",
                discount: "Enter a valid discount"
            }));
            calculateAmount(packageData.packageType);
        } else {
            setPackageError(prev => ({
                ...prev,
                totalAmount: "",
                discount: ""
            }));
            setPackage(prev => ({ ...prev, totalAmount: changeAmount }));
        }
    }, [packageData.discount]);

    const onConsigneeChange = (event, values) => {
        if (!values) return;
        
        if (values.label === "General") {
            setUiState(prev => ({ ...prev, showConsigneeForm: true }));
            setPackage(prev => ({
                ...prev,
                consigneeName: 'General',
                consigneePinCode: '',
                consigneeAddress: '',
                consigneeDistrict: '',
                consigneeState: '',
                consigneeCountry: '',
                consigneeContactNo: '',
                consigneeEmail: '',
                consigneeWhatsappNo: '',
                consigneeId: 'General',
            }));
        } else if (values.value) {
            setUiState(prev => ({ ...prev, showConsigneeForm: false }));
            setPackage(prev => ({
                ...prev,
                consigneeName: values.label, // Store the label for display
                consigneePinCode: values.value.pinCode,
                consigneeAddress: values.value.address,
                consigneeDistrict: values.value.district,
                consigneeState: values.value.state,
                consigneeCountry: values.value.country,
                consigneeContactNo: values.value.contactNo,
                consigneeEmail: values.value.email,
                consigneeWhatsappNo: values.value.whatsappNo,
                consigneeId: values.value.id        
            }));
        }
    };

    const handlePincodeValidation = async (value, type) => {
        if (value.length !== 6) return;
        
        const data = await fetchPincodeData(value);
        if (!data) return;
        
        if (data.Status === "Error") {
            setPackageError(prev => ({
                ...prev,
                [type === "sender" ? "senderPinCode" : "consigneePincode"]: 'Correct pincode is required!'
            }));
            return;
        }
        
        const locationData = {
            state: data.PostOffice[0].State,
            district: data.PostOffice[0].District,
            country: data.PostOffice[0].Country
        };
        
        if (type === "consignee") {
            setPackage(prev => ({
                ...prev,
                consigneeState: locationData.state,
                consigneeDistrict: locationData.district,
                consigneeCountry: locationData.country
            }));
        } else if (type === "sender") {
            setPackage(prev => ({
                ...prev,
                senderState: locationData.state,
                senderDistrict: locationData.district,
                senderCountry: locationData.country
            }));
        }
    };

    const onClickPackageType = (event, values) => {
        if (!values) return;
        
        setPackage(prev => ({ ...prev, packageType: values.label || '' }));
        calculateAmount(values.label);
    };
    
    const calculateAmount = (value) => {
        const type = value || 'Local';
        let amount = 0;
        
        switch (type) {
            case 'Local':
                amount = calculateTotal(calculation.localRate);
                break;
            case 'State':
                amount = calculateTotal(calculation.stateRate);
                break;
            case 'National':
                amount = calculateTotal(calculation.nationalRate);
                break;
            case 'International':
                amount = calculateTotal(calculation.interNationalRate);
                break;
            default:
                amount = 0;
        }
    
        setPackage(prev => ({
            ...prev,
            totalAmount: amount,
        }));
    };
    
    const calculateTotal = (value) => {
        const weight = parseFloat(packageData.weight) || 0;
        const discount = parseFloat(packageData.discount) || 0;
        const rate = parseFloat(value) || 0;
    
        const amountWithoutGst = rate * weight;
        const amountWithGst = (rate * weight * 18) / 100;
        const total = amountWithoutGst + amountWithGst - discount;
    
        setPackage(prev => ({
            ...prev,
            amount: amountWithoutGst,
            totalGST: amountWithGst,
        }));
    
        return total;
    };
    
   

    const onClickCancel = () => {
        setUiState(prev => ({ ...prev, showQR: false, showInvoice: false, showSenderForm: true, showConsigneeForm: false }));
        setPackage(INITIAL_PACKAGE_STATE);
        setPackageError(INITIAL_ERROR_STATE);
        setResponseData(null);
        setTabs([]);
        setActiveTab(-1);
        // setClients([]);
        setCalculation({
            localRate: "",
            stateRate: "",
            nationalRate: "",
            interNationalRate: ""
        });
        setPackage(INITIAL_PACKAGE_STATE);
        setPackageError(INITIAL_ERROR_STATE);
        setResponseData(null);
        setTabs([]);
        setActiveTab(-1);
        // setClients([]);
        setCalculation({
            localRate: "",
            stateRate: "",
            nationalRate: "",
            interNationalRate: ""
        });
        setPackage(INITIAL_PACKAGE_STATE);
        setPackageError(INITIAL_ERROR_STATE);
        setResponseData(null);
        setTabs([]);
        setActiveTab(-1);
        // setClients([]);
        setCalculation({
            localRate: "",
            stateRate: "",
            nationalRate: "",
            interNationalRate: ""
        });
        setPackage(INITIAL_PACKAGE_STATE);
        setPackageError(INITIAL_ERROR_STATE);
        setResponseData(null);
        setTabs([]);
        setActiveTab(-1);
        // setClients([]);
        setCalculation({
            localRate: "",
            stateRate: "",
            nationalRate: "",
            interNationalRate: ""
        });
        setPackage(INITIAL_PACKAGE_STATE);
        setPackageError(INITIAL_ERROR_STATE);
        setResponseData(null);
        setTabs([]);
        setActiveTab(-1);
        //  setClients([]);

    }

    const onChangePackage = (event) => {
        const { name, value } = event.target;
        
        // Validate field
        const errorMessage = validateField(name, value);
        setPackageError(prev => ({
            ...prev,
            [name]: errorMessage
        }));
        
        // Handle pincode validation
        if (name === 'consigneePincode' && value.length === 6) {
            handlePincodeValidation(value, "consignee");
        } else if (name === 'senderPinCode' && value.length === 6) {
            handlePincodeValidation(value, "sender");
        }
        
        // Update package state
        setPackage(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const onChangeToPay = (event) => {
        setPackage(prev => ({ ...prev, toPay: event.target.checked }));
    };
    const onClickAddPackage = async () => {
        const submitData = {
            consigneeName: packageData.consigneeName === "General" ? packageData.receiverName : packageData.consigneeName,
            consigneeAddress: packageData.consigneeAddress,
            consigneePinCode: packageData.consigneePinCode,
            consigneeDistrict: packageData.consigneeDistrict,
            consigneeState: packageData.consigneeState,
            consigneeCountry: packageData.consigneeCountry,
            consigneeContactNo: packageData.consigneeContactNo,
            consigneeEmail: packageData.consigneeEmail,
            consigneeWhatsappNo: packageData.consigneeWhatsappNo,
            consigneeId: packageData.consigneeId,
            
            consignmentNo: packageData.consignmentNo,

            senderName: packageData.clientName === "General" ? packageData.senderName : packageData.clientName,
            sub_packages: tabs,
            gstNo: packageData.gstNo,
            senderPinCode: packageData.senderPinCode,
            senderAddress: packageData.senderAddress,
            senderDistrict: packageData.senderDistrict,
            senderState: packageData.senderState,
            senderCountry: packageData.senderCountry,
            senderContactNo: packageData.senderContactNo,
            senderEmail: packageData.senderEmail,
            senderWhatsappNo: packageData.senderWhatsappNo,
            senderId: packageData.senderId,

            paymentType: packageData.paymentType,

            weight: packageData.weight,

            gstAmount: packageData.totalGST,
            amount: packageData.amount,
            discount: packageData.discount,
            totalAmount: packageData.totalAmount,
            packageType: packageData.packageType
        };

        try {
            const response = await submitPackage(submitData);
            setResponseData(response);
            setUiState(prev => ({ ...prev, showQR: true }));
        } catch (error) {
            if (error.message === "Your account was de-activated, please contact support!") {
                toast.error("Your account was de-activated, please contact support!");
            } else {
                toast.error(getApiErrorMessage(error, "Failed to add package. Please try again."));
            }
        }
    };

    // Print functionality
    const contentRef = useRef(null);
    const promiseResolveRef = useRef(null);
    const promiseResolveRefInvoice = useRef(null);

    const handlePrintQR = useReactToPrint({
        contentRef,
        onBeforePrint: () => new Promise((resolve) => {
            promiseResolveRef.current = resolve;
            setUiState(prev => ({ ...prev, isPrinting: true }));
            }),
            onAfterPrint: () => {
                promiseResolveRef.current = null;
            setUiState(prev => ({ 
                ...prev, 
                isPrinting: false,
                showInvoice: true,
                showQR: false 
            }));
        }
    });

    const handlePrintInvoice = useReactToPrint({
        contentRef,
        onBeforePrint: () => new Promise((resolve) => {
            promiseResolveRefInvoice.current = resolve;
            setUiState(prev => ({ ...prev, isPrintingInvoice: true }));
        }),
        onAfterPrint: () => {
            promiseResolveRefInvoice.current = null;
            setUiState(prev => ({ 
                ...prev, 
                showQR: false,
                showInvoice: false,
                isPrintingInvoice: false 
            }));
        }
    });

    // Print effect handlers
    useEffect(() => {
        if (uiState.isPrinting && promiseResolveRef.current) {
            promiseResolveRef.current();
        }
    }, [uiState.isPrinting]);

    useEffect(() => {
        if (uiState.isPrintingInvoice && promiseResolveRefInvoice.current) {
            promiseResolveRefInvoice.current();
        }
    }, [uiState.isPrintingInvoice]);
    
    useEffect(() => {
        if (uiState.showQR) {
            handlePrintQR();
        }
    }, [uiState.showQR]);

    useEffect(() => {
        if (uiState.showInvoice) {
            handlePrintInvoice();
        }
    }, [uiState.showInvoice]);







    // for edit data

    useEffect(() => {
        if (editData && editData.editData) {
            setShowEditData(true);
            const updatedPackage = { ...editData.editData };

            // Handle sender/client form visibility
            if (updatedPackage.senderName === "General") {
                setUiState(prev => ({ ...prev, showSenderForm: true }));
            } else if (updatedPackage.senderName) {
                setUiState(prev => ({ ...prev, showSenderForm: false }));
            }

            // Handle consignee form visibility
            if (updatedPackage.consigneeName === "General") {
                setUiState(prev => ({ ...prev, showConsigneeForm: true }));
            } else if (updatedPackage.consigneeName) {
                setUiState(prev => ({ ...prev, showConsigneeForm: false }));
            }

            // If there are subPackages in the editData, set the tabs state to show each as a tab
            if (updatedPackage.subPackages && Array.isArray(updatedPackage.subPackages) && updatedPackage.subPackages.length > 0) {
                setTabs(updatedPackage.subPackages.map(pkg => ({
                    label: pkg.label,
                    data: pkg.data
                })));
                setActiveTab(0);
            }
            

            setPackage(updatedPackage);
        }
    }, [editData]);

    const onClickBack = () => {
        navigate('/packages');
    };

    const onClickEditPackage = async () => {
        const submitData = {
            consigneeName: packageData.consigneeName === "General" ? packageData.receiverName : packageData.consigneeName,
            consigneeAddress: packageData.consigneeAddress,
            consigneePinCode: packageData.consigneePinCode,
            consigneeDistrict: packageData.consigneeDistrict,
            consigneeState: packageData.consigneeState,
            consigneeCountry: packageData.consigneeCountry,
            consigneeContactNo: packageData.consigneeContactNo,
            consigneeEmail: packageData.consigneeEmail,
            consigneeWhatsappNo: packageData.consigneeWhatsappNo,
            consigneeId: packageData.consigneeId,

            consignmentNo: packageData.consignmentNo,

            senderName: packageData.clientName === "General" ? packageData.senderName : packageData.clientName,
            sub_packages: tabs,
            gstNo: packageData.gstNo,
            senderPinCode: packageData.senderPinCode,
            senderAddress: packageData.senderAddress,
            senderDistrict: packageData.senderDistrict,
            senderState: packageData.senderState,
            senderCountry: packageData.senderCountry,
            senderContactNo: packageData.senderContactNo,
            senderEmail: packageData.senderEmail,
            senderWhatsappNo: packageData.senderWhatsappNo,
            senderId: packageData.senderId,

            toPay: packageData.toPay,
            weight: packageData.weight,
            discount: packageData.discount,
            totalAmount: packageData.totalAmount,
            packageType: packageData.packageType,

            id: packageData.id,
        };

        try {
            const response = await updatePackage(submitData);
            setResponseData(response);
            setUiState(prev => ({ ...prev, showQR: true }));
        } catch (error) {
            if (error.message === "Your account was de-activated, please contact support!") {
                toast.error("Your account was de-activated, please contact support!");
            } else {
                toast.error(getApiErrorMessage(error, "Failed to add package. Please try again."));
            }
        }
    };


    return (
        <>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        {showEditData ? "Edit Package" : "Add Package"}
                    </Typography>

                </Stack>

                <Stack>
                     <TextField id="outlined-basic" fullWidth label="Consignment Number" variant="outlined" style={{ marginRight: "10px" }}
                        name='consignmentNo'
                        onChange={onChangePackage}
                        disabled = {showEditData && true}
                        value={packageData.consignmentNo}
                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>

                    <Autocomplete
                        disablePortal
                        id="consignee-autocomplete"
                        options={consigneeClients}
                        style={{ marginRight: "10px" }}
                        onChange={onConsigneeChange}
                        fullWidth
                        value={consigneeClients.find(client => client.label === packageData.consigneeName) || null}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.label === value?.label}
                        renderInput={(params) => <TextField  {...params} label="Receiver Name" />}
                    />

                    {/* <TextField id="outlined-basic" fullWidth label="Consignee Name" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeName'
                        error={showPackageError.consigneeName.length > 0 ? true : false}
                        helperText={showPackageError.consigneeName.length > 0 ? showPackageError.consigneeName : ''}
                        onChange={onChangePackage}

                    /> */}
                    {uiState.showConsigneeForm && <TextField id="outlined-basic" name='receiverName' fullWidth label="Receiver Name" onChange={onChangePackage} variant="outlined" style={{ marginRight: "10px" }}

                    />}
                    <TextField id="outlined-basic" fullWidth label=" Receiver Address" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeAddress'
                        error={packageErrors.consigneeAddress.length > 0 ? true : false}
                        helperText={packageErrors.consigneeAddress.length > 0 ? packageErrors.consigneeAddress : ''}
                        onChange={onChangePackage}
                        value={packageData.consigneeAddress}
                    />
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField fullWidth id="outlined-basic" label="Receiver Pin Code" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneePinCode'
                        type='number'
                        error={packageErrors.consigneePinCode.length > 0 ? true : false}
                        helperText={packageErrors.consigneePinCode.length > 0 ? packageErrors.consigneePinCode : ''}
                        onChange={onChangePackage}
                        value={packageData.consigneePinCode}
                    />
                    <TextField fullWidth id="outlined-basic" label="Receiver District" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeDistrict'
                        onChange={onChangePackage}
                        value={packageData.consigneeDistrict}
                        error={packageErrors.consigneeDistrict.length > 0 ? true : false}
                        helperText={packageErrors.consigneeDistrict.length > 0 ? packageErrors.consigneeDistrict : ''}
                    />
                    <TextField id="outlined-basic" fullWidth label="Receiver State" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeState'
                        onChange={onChangePackage}
                        value={packageData.consigneeState}
                        error={packageErrors.consigneeState.length > 0 ? true : false}
                        helperText={packageErrors.consigneeState.length > 0 ? packageErrors.consigneeState : ''}
                    />
                    <TextField fullWidth id="outlined-basic" label="Receiver Country" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeCountry'
                        onChange={onChangePackage}
                        value={packageData.consigneeCountry}
                        error={packageErrors.consigneeCountry.length > 0 ? true : false}
                        helperText={packageErrors.consigneeCountry.length > 0 ? packageErrors.consigneeCountry : ''}
                    />
                </Stack>


                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField id="outlined-basic" fullWidth label="Receiver Contact No" variant="outlined" style={{ marginRight: "10px" }}
                        type='number'
                        name='consigneeContactNo'
                        onChange={onChangePackage}
                        value={packageData.consigneeContactNo}
                        error={packageErrors.consigneeContactNo.length > 0 ? true : false}
                        helperText={packageErrors.consigneeContactNo.length > 0 ? packageErrors.consigneeContactNo : ''}

                    />
                    <TextField fullWidth id="outlined-basic" label="Receiver Email" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeEmail'
                        type='email'
                        onChange={onChangePackage}
                        value={packageData.consigneeEmail}
                        error={packageErrors.consigneeEmail.length > 0 ? true : false}
                        helperText={packageErrors.consigneeEmail.length > 0 ? packageErrors.consigneeEmail : ''}
                    />
                    <TextField id="outlined-basic" fullWidth label="Receiver Whatsapp No" variant="outlined" style={{ marginRight: "10px" }}
                        name='consigneeWhatsappNo'
                        type='number'
                        onChange={onChangePackage}
                        value={packageData.consigneeWhatsappNo}
                        error={packageErrors.consigneeWhatsappNo.length > 0 ? true : false}
                        helperText={packageErrors.consigneeWhatsappNo.length > 0 ? packageErrors.consigneeWhatsappNo : ''}
                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }} style={{ marginTop: "50px" }}>

                    <Autocomplete
                        disablePortal
                        id="sender-autocomplete"
                        options={clients}
                        style={{ marginRight: "10px" }}
                        onChange={onClientChange}
                        fullWidth
                        value={clients.find(client => client.label === packageData.clientName || packageData.senderName) || null}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.label === value?.label}
                        renderInput={(params) => <TextField  {...params} label="Sender Client" />}
                    />
                    {uiState.showSenderForm && <TextField id="outlined-basic" fullWidth label="Sender Name" variant="outlined" style={{ marginRight: "10px" }}

                    />}
                    <TextField id="outlined-basic" fullWidth label="GST No" variant="outlined" style={{ marginRight: "10px" }}
                        name='gstNo'
                        value={packageData.gstNo}
                        error={packageErrors.gstNo.length > 0 ? true : false}
                        helperText={packageErrors.gstNo.length > 0 ? packageErrors.gstNo : ''}
                        onChange={onChangePackage}

                    />


                </Stack>
                <Stack direction="row" justifyContent="space-between" >

                    <TextField fullWidth id="outlined-basic" label="Sender Address" variant="outlined" style={{ marginRight: "10px" }}
                        name='senderAddress'
                        error={packageErrors.senderAddress.length > 0 ? true : false}
                        helperText={packageErrors.senderAddress.length > 0 ? packageErrors.senderAddress : ""}
                        value={packageData.senderAddress} onChange={onChangePackage}

                    />
                    <TextField id="outlined-basic" fullWidth label="Sender Pincode" variant="outlined" style={{ marginRight: "10px" }}
                        name='senderPinCode'
                        error={packageErrors.senderPinCode.length > 0 ? true : false}
                        helperText={packageErrors.senderPinCode.length > 0 ? packageErrors.senderPinCode : ""}
                        value={packageData.senderPinCode}
                        onChange={onChangePackage}
                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField id="outlined-basic" fullWidth label="Sender District" variant="outlined" style={{ marginRight: "10px" }}
                        name='senderDistrict'
                        value={packageData.senderDistrict}
                        error={packageErrors.senderDistrict.length > 0 ? true : false}
                        helperText={packageErrors.senderDistrict.length > 0 ? packageErrors.senderDistrict : ""}
                        onChange={onChangePackage}
                    />
                    <TextField fullWidth id="outlined-basic" label="Sender State" variant="outlined" style={{ marginRight: "10px" }}
                        value={packageData.senderState}
                        name='senderState'
                        error={packageErrors.senderState.length > 0 ? true : false}
                        helperText={packageErrors.senderState.length > 0 ? packageErrors.senderState : ""}
                        onChange={onChangePackage}

                    />
                    <TextField id="outlined-basic" fullWidth label="Sender Country" variant="outlined" style={{ marginRight: "10px" }}
                        value={packageData.senderCountry}
                        name='senderCountry'
                        error={packageErrors.senderCountry.length > 0 ? true : false}
                        helperText={packageErrors.senderCountry.length > 0 ? packageErrors.senderCountry : ""}
                        onChange={onChangePackage}

                    />
                </Stack> <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField id="outlined-basic" fullWidth label="Sender Contact No" variant="outlined" style={{ marginRight: "10px" }}
                        value={packageData.senderContactNo}
                        error={packageErrors.senderContactNo.length > 0 ? true : false}
                        name='senderContactNo'
                        helperText={packageErrors.senderContactNo.length > 0 ? packageErrors.senderContactNo : ""}
                        onChange={onChangePackage}


                    />
                    <TextField fullWidth id="outlined-basic" label="Sender Email" variant="outlined" style={{ marginRight: "10px" }}
                          value={packageData.senderEmail}
                          name='senderEmail'
                          error={packageErrors.senderEmail.length > 0 ? true : false}
                          helperText={packageErrors.senderEmail.length > 0 ? packageErrors.senderEmail : ""}
                          onChange={onChangePackage}

                    />
                    <TextField id="outlined-basic" fullWidth label="Sender Whatsapp No" variant="outlined" style={{ marginRight: "10px" }}
                        name='senderWhatsappNo'
                        value={packageData.senderWhatsappNo}
                        error={packageErrors.senderWhatsappNo.length > 0 ? true : false}
                        helperText={packageErrors.senderWhatsappNo.length > 0 ? packageErrors.senderWhatsappNo : ""}
                        onChange={onChangePackage}

                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" style={{ marginTop: "50px" }}>
                    <Autocomplete
                        disablePortal
                        disableClearable
                        id="package-type-autocomplete"
                        options={PACKAGE_TYPES}
                        style={{ marginRight: "10px" }}
                        onChange={onClickPackageType}
                        onKeyDown={(e) => e.preventDefault()}
                        fullWidth
                        value={PACKAGE_TYPES.find((type) => type.label === packageData.packageType) || PACKAGE_TYPES[0]}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.label === value?.label}
                        renderInput={(params) => (
                            <TextField {...params} label="Package Type" InputProps={{ ...params.InputProps, readOnly: true }} />
                        )}
                    />

                    <TextField fullWidth id="outlined-basic" label="Weight (in gm)" variant="outlined" style={{ marginRight: "10px" }}
                        name='weight'
                        type='number'
                        value={packageData.weight}
                        onChange={onChangePackage}
                        error={packageErrors.weight.length > 0 ? true : false}
                        helperText={packageErrors.weight.length > 0 ? packageErrors.weight : ""}


                    />
                    <TextField id="outlined-basic" fullWidth label="Amount (in INR)" variant="outlined" style={{ marginRight: "10px" }}
                        name='amount'
                        type='number'
                        value={packageData.amount}
                        onChange={onChangePackage}
                        error={packageErrors.amount.length > 0 ? true : false}
                        helperText={packageErrors.amount.length > 0 ? packageErrors.amount : ""}
                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField id="outlined-basic" fullWidth label="Gst Amount (cgst + sgst + igst)(in INR)" variant="outlined" style={{ marginRight: "10px" }}
                        name='totalGST'
                        type='number'
                        value={packageData.totalGST}
                        onChange={onChangePackage}
                        error={packageErrors.totalGST.length > 0 ? true : false}
                        helperText={packageErrors.totalGST.length > 0 ? packageErrors.totalGST : ""}

                    />
                    <TextField id="outlined-basic" fullWidth label="Discount (in INR)" variant="outlined" style={{ marginRight: "10px" }}
                        name='discount'
                        type='number'
                        value={packageData.discount}
                        onChange={onChangePackage}
                        error={packageErrors.discount.length > 0 ? true : false}
                        helperText={packageErrors.discount.length > 0 ? packageErrors.discount : ""}


                    />

                    <TextField id="outlined-basic" fullWidth label="Total Amount (in INR)" variant="outlined" style={{ marginRight: "10px" }}
                        name='totalAmount'
                        type='number'
                        value={(packageData.totalAmount)}
                        onChange={onChangePackage}
                        error={packageErrors.totalAmount.length > 0 ? true : false}
                        helperText={packageErrors.totalAmount.length > 0 ? packageErrors.totalAmount : ""}

                    />
                </Stack>

                

                <Stack>
                    <FormControl component="fieldset" sx={{ display: 'block', mt: 2 }}>
                        <RadioGroup
                            row
                            aria-label="payment-type"
                            name="paymentType"
                            value={packageData.paymentType || 'Credit'}
                            onChange={(e) => {
                                const value = e.target.value;
                                setPackage(prev => ({
                                    ...prev,
                                    paymentType: value
                                }));
                            }}
                        >
                            <FormControlLabel value="Credit" control={<Radio color="primary" />} label="Credit" />
                            <FormControlLabel value="To Pay" control={<Radio color="primary" />} label="To Pay" />
                            <FormControlLabel value="Cash" control={<Radio color="primary" />} label="Cash" />
                        </RadioGroup>
                    </FormControl>

                </Stack>
                
                <Stack>

                    <Tabs value={activeTab} onChange={handleChangeTab} aria-label="basic tabs example">
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                label={
                                    <div>
                                        {tab.label}
                                        <IconButton key={index} onClick={() => handleDeleteTab(index)} aria-label="delete">
                                            <Iconify icon={'charm:cross'} />
                                        </IconButton>
                                    </div>
                                }
                            />
                        ))}
                        <Button variant="success" onClick={handleAddTab}>+</Button>
                    </Tabs>

                    <div>
                        {tabs.map((tab, index) => (
                            <div key={index} hidden={activeTab !== index}>
                                <textarea
                                    value={tab.data}
                                    onChange={(e) => handleDataChange(index, e.target.value)}
                                    rows={4}
                                    cols={50}
                                />
                            </div>
                        ))}
                    </div>
                </Stack>
                {showEditData && (
                    <Stack direction="row" alignItems="center" justifyContent={'center'} mt={5} mb={5}>
                        <Button variant="contained" style={{ marginRight: "10px" }} onClick={onClickEditPackage}>
                            Edit
                        </Button>
                        <Button variant="contained" color='error' onClick={onClickBack}>Back</Button>
                    </Stack>
                )}
                {!showEditData && (
                <Stack direction="row" alignItems="center" justifyContent={'center'} mt={5} mb={5}>
                    <Button variant="contained" style={{ marginRight: "10px" }} onClick={onClickAddPackage}>
                        Add
                    </Button>
                    <Button variant="contained" color='error' onClick={onClickCancel}>
                        Cancel
                    </Button>
                </Stack>)}
                     
               {uiState.showQR &&  
                    <div ref={contentRef}  >
                      <PrintQRComponent  showResponseData={responseData} />
                    </div>
                }

{uiState.showInvoice &&  
                    <div ref={contentRef}  >
                      <PrintInvoiceComponent  showResponseData={responseData} />
                    </div>
                }
               
            </Container>


        </>

    )
}
